import { api, ApiError, getMe, listAccounts } from '../api-client';
import type { MeResponse, AccountListItem } from '../api-client';
import { navigate } from '../router';
import { openPayCardSheet } from '../components/pay-card-sheet';

interface HoldingsByType {
  crypto: number;
  equity: number;
  etf: number;
  property: number;
  alternative: number;
}

interface HoldingDetail {
  id: string;
  asset_symbol: string;
  asset_name: string;
  asset_type: keyof HoldingsByType;
  quantity: number;
  last_price_aed: number | null;
  last_price_updated_at: string | null;
  value_aed: number;
}

interface ReconcileAccountRow {
  account_id: string;
  account_name: string;
  kind: 'asset' | 'liability';
  current_balance_aed: number;
  has_holdings: boolean;
}

interface ReconcileResponse {
  as_of: string;
  accounts: ReconcileAccountRow[];
  net_worth_aed: number;
  holdings_total_aed: number;
  holdings_by_type: HoldingsByType;
  holdings: HoldingDetail[];
  prices_as_of: string | null;
  price_sources: ReadonlyArray<'manual' | 'coingecko' | 'yahoo'>;
}

const TYPE_LABELS: Record<keyof HoldingsByType, string> = {
  crypto:      'Crypto',
  equity:      'Stocks',
  etf:         'ETFs',
  property:    'Property',
  alternative: 'Alternative',
};

export function renderDashboard(root: HTMLElement): void {
  root.innerHTML = `
    <div class="dashboard">
      <header class="dashboard-header">
        <button class="back-btn" id="dash-back" aria-label="Back">←</button>
        <h1 class="dashboard-title">Dashboard</h1>
      </header>
      <div class="dashboard-body" id="dash-body">${skeletonHtml()}</div>
    </div>
  `;

  root.querySelector<HTMLButtonElement>('#dash-back')!.addEventListener('click', () => {
    void navigate('/');
  });

  void load(root.querySelector<HTMLElement>('#dash-body')!);
}

async function load(host: HTMLElement): Promise<void> {
  try {
    const [reconcile, me, accountsResp] = await Promise.all([
      api<ReconcileResponse>('/api/reconcile'),
      getMe(),
      listAccounts(),
    ]);
    host.innerHTML = renderBody(reconcile);
    wireHoldings(host);
    wireLiabilityTaps(host, reconcile, me, accountsResp.accounts);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      void navigate('/');
      return;
    }
    host.innerHTML = `
      <div class="dash-error">
        <p>Couldn't load dashboard.</p>
        <button id="dash-retry" class="primary">Retry</button>
      </div>
    `;
    host.querySelector<HTMLButtonElement>('#dash-retry')!.addEventListener('click', () => {
      host.innerHTML = skeletonHtml();
      void load(host);
    });
  }
}

function skeletonHtml(): string {
  return `
    <div class="dash-skeleton">
      <div class="sk sk-headline"></div>
      <div class="sk sk-row"></div>
      <div class="sk sk-row"></div>
      <div class="sk sk-row"></div>
      <div class="sk sk-row"></div>
    </div>
  `;
}

function renderBody(data: ReconcileResponse): string {
  const liabilities = data.accounts
    .filter((a) => a.kind === 'liability')
    .sort((a, b) => b.current_balance_aed - a.current_balance_aed);
  const assets = data.accounts
    .filter((a) => a.kind === 'asset')
    .sort((a, b) => b.current_balance_aed - a.current_balance_aed);

  return `
    ${renderHeadline(data)}
    ${renderInvestments(data)}
    ${renderSection('Cards', liabilities, 'liability')}
    ${renderSection('Bank & Cash', assets, 'asset')}
  `;
}

function renderHeadline(data: ReconcileResponse): string {
  const nw = formatAedNoDecimals(data.net_worth_aed);
  return `
    <section class="dash-headline">
      <div class="dash-nw">${nw}</div>
      <div class="dash-nw-label muted">Net worth</div>
      <div class="dash-asof muted">${formatAsOf(data.as_of)}</div>
    </section>
  `;
}

function renderInvestments(data: ReconcileResponse): string {
  const total = formatAed(data.holdings_total_aed);
  const stamp = data.prices_as_of
    ? `<div class="dash-prices-asof muted">Prices as of ${formatPriceStamp(data.prices_as_of)}${formatSources(data.price_sources)}</div>`
    : '';
  const expandedRows = renderHoldingsExpanded(data);
  const noPricesNote = data.prices_as_of === null
    ? '<div class="dash-no-prices muted">No prices recorded yet.</div>'
    : '';
  const missingCount = data.holdings.filter((h) => h.last_price_aed == null).length;
  const partialNote = (data.prices_as_of !== null && missingCount > 0)
    ? `<div class="dash-missing muted">${missingCount} holding${missingCount === 1 ? '' : 's'} missing prices</div>`
    : '';

  return `
    <section class="dash-section dash-investments">
      <div class="dash-section-header dash-toggle" id="dash-inv-toggle" role="button" tabindex="0" aria-expanded="false">
        <span class="dash-section-title">Investments</span>
      </div>
      <div class="dash-row dash-toggle-summary">
        <span class="dash-row-name">${total}</span>
        <span class="dash-chevron" id="dash-inv-chev">▾</span>
      </div>
      <div class="dash-investments-detail" id="dash-inv-detail" hidden>
        ${expandedRows}
        ${noPricesNote}
      </div>
      ${stamp}
      ${partialNote}
    </section>
  `;
}

function renderHoldingsExpanded(data: ReconcileResponse): string {
  const types: Array<keyof HoldingsByType> = ['crypto', 'equity', 'etf', 'property', 'alternative'];
  return types
    .filter((t) => data.holdings_by_type[t] > 0)
    .map((t) => `
      <div class="dash-row">
        <span class="dash-row-name">${TYPE_LABELS[t]}</span>
        <span class="dash-row-amount">${formatAed(data.holdings_by_type[t])}</span>
      </div>
    `)
    .join('');
}

function renderSection(title: string, rows: ReconcileAccountRow[], kind: 'asset' | 'liability'): string {
  const visible = kind === 'asset'
    ? rows.filter((r) => !(r.current_balance_aed === 0 && r.has_holdings))
    : rows;

  if (visible.length === 0) return '';

  const inner = visible.map((r) => {
    const display = kind === 'liability'
      ? formatAedSigned(r.current_balance_aed)
      : formatAed(r.current_balance_aed);
    const tappable = kind === 'liability' ? ' dash-row-tappable' : '';
    const chevron = kind === 'liability'
      ? '<span class="dash-row-chevron" aria-hidden="true">›</span>'
      : '';
    const role = kind === 'liability' ? ' role="button" tabindex="0"' : '';
    const aria = kind === 'liability'
      ? ` aria-label="Pay ${escapeHtml(r.account_name)}"`
      : '';
    return `
      <div class="dash-row${tappable}" data-account-id="${escapeHtml(r.account_id)}" data-account-kind="${kind}"${role}${aria}>
        <span class="dash-row-name">${escapeHtml(r.account_name)}</span>
        <span class="dash-row-amount">${display}</span>${chevron}
      </div>
    `;
  }).join('');
  return `
    <section class="dash-section">
      <div class="dash-section-header"><span class="dash-section-title">${title}</span></div>
      ${inner}
    </section>
  `;
}

function wireHoldings(host: HTMLElement): void {
  const toggle = host.querySelector<HTMLElement>('#dash-inv-toggle');
  const detail = host.querySelector<HTMLElement>('#dash-inv-detail');
  const chev = host.querySelector<HTMLElement>('#dash-inv-chev');
  if (!toggle || !detail || !chev) return;
  const flip = () => {
    const open = !detail.hasAttribute('hidden');
    if (open) {
      detail.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', 'false');
      chev.textContent = '▾';
    } else {
      detail.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
      chev.textContent = '▴';
    }
  };
  toggle.addEventListener('click', flip);
  toggle.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
      e.preventDefault();
      flip();
    }
  });
  // The chevron lives in a sibling row; tapping it should also toggle.
  const summary = host.querySelector<HTMLElement>('.dash-toggle-summary');
  if (summary) summary.addEventListener('click', flip);
}

function wireLiabilityTaps(
  host: HTMLElement,
  reconcile: ReconcileResponse,
  me: MeResponse,
  accounts: ReadonlyArray<AccountListItem>,
): void {
  host.querySelectorAll<HTMLElement>('.dash-row[data-account-kind="liability"]').forEach((row) => {
    const open = () => {
      const accountId = row.getAttribute('data-account-id');
      if (!accountId) return;
      const reconcileRow = reconcile.accounts.find((a) => a.account_id === accountId);
      if (!reconcileRow) return;
      const sheetMount = document.createElement('div');
      document.body.appendChild(sheetMount);
      openPayCardSheet(sheetMount, {
        card: {
          id: reconcileRow.account_id,
          name: reconcileRow.account_name,
          current_balance_aed: reconcileRow.current_balance_aed,
        },
        defaultFundingAccountId: me.default_funding_account_id,
        accounts,
        onClose: () => sheetMount.remove(),
        onSuccess: () => {
          sheetMount.remove();
          host.innerHTML = skeletonHtml();
          void load(host);
        },
      });
    };
    row.addEventListener('click', open);
    row.addEventListener('keydown', (e) => {
      const key = (e as KeyboardEvent).key;
      if (key === 'Enter' || key === ' ') {
        e.preventDefault();
        open();
      }
    });
  });
}

function formatAed(n: number): string {
  return `AED ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatAedNoDecimals(n: number): string {
  return `AED ${Math.round(n).toLocaleString('en-US')}`;
}

function formatAedSigned(n: number): string {
  // Always shows sign for liabilities-as-negative; preserves "AED -8,432.10".
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `AED -${abs}` : `AED ${abs}`;
}

function formatAsOf(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const day = d.getDate();
  const month = d.toLocaleString('en-US', { month: 'short' });
  return `as of ${hh}:${mm} · ${day} ${month}`;
}

function formatPriceStamp(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' })}`;
}

function formatSources(sources: ReadonlyArray<string>): string {
  if (!sources.length) return '';
  return ` (${sources.join(' + ')})`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
